/**
 * Driver catalog for the Forecast V3 driver picker.
 *
 * `desc` / `formula` / `inputHint` are the user-facing explanation of what a
 * driver DOES. Every line here was written against the driver's own
 * `calculate()` in azfApi/utils/forecastCalculatorV3/drivers/ — not against the
 * driver's name. Where a driver silently falls back (Budgeted $POR reverting to
 * the raw budget, $ LM walking
 * back to the latest closed month) the fallback is stated, because the number
 * the user ends up looking at is often the fallback rather than the formula.
 *
 * Keep in sync with:
 *   - drivers/<Driver>.js getHelperText()  (same text, API/AI surface)
 *   - drivers/driverEligibility.js RULES   (applicableTo — the backend refuses
 *     ineligible assignments, so the picker must not offer them)
 */

import { LY_BUDGET_DRIVER_BASES } from '../../types/driverTerms';
import { DRIVER_CONFIG } from '../../types';
import type { DriverType } from '../../types';

export interface DriverCatalogEntry {
  /** The driverType value persisted on forecastDriverAssignment. */
  id: DriverType;
  /** Group heading in the picker. */
  group: string;
  label: string;
  /** One line: what it multiplies, against which period, and where it falls back. */
  desc: string;
  /** Mono chip on the right of the row — the math, short. */
  tag: string;
  /** Shown above the Driver Inputs field when this driver is selected. */
  inputHint: string;
  /** The `= …` note under Driver Inputs. */
  formula: string;
  /** Extra search terms beyond label/desc/tag. */
  keywords?: string;
}

export const DRIVER_GROUPS: { name: string; hint: string }[] = [
  { name: 'Enter Manually', hint: 'you type the number' },
  { name: 'Driven by Rooms', hint: 'scales with occupancy' },
  { name: 'Driven by History', hint: 'last year, then last month' },
  { name: 'Driven by Budget', hint: 'follows the approved plan' },
  { name: 'Driven by Revenue', hint: 'a share of revenue' },
  { name: 'Driven by Another Account', hint: 'linked to a sibling line' },
  { name: 'Composed', hint: 'stacked layers' },
  { name: 'Labor', hint: 'labor accounts only' },
];

export const DRIVER_CATALOG: DriverCatalogEntry[] = [
  // ── Enter Manually ────────────────────────────────────────────────────────
  {
    id: 'Manual',
    group: 'Enter Manually',
    label: 'Manual Entry',
    desc: 'Type a different amount for each month',
    tag: 'per month',
    inputHint: 'Enter an amount directly in each month you want to set.',
    formula: 'Forecast = the amount you type in each month',
    keywords: 'type enter blank empty',
  },
  {
    id: 'Static Entry',
    group: 'Enter Manually',
    label: 'Static Entry',
    desc: 'One fixed amount, repeated in every month',
    tag: 'fixed',
    inputHint: 'The same amount is applied to all months.',
    formula: 'Forecast = Input Value (identical in every month)',
    keywords: 'flat constant same',
  },

  // ── Driven by Rooms ───────────────────────────────────────────────────────
  {
    id: '$ / ORN',
    group: 'Driven by Rooms',
    label: '$POR — $ per occupied room',
    // DollarOrn.js: PMS daily stats are extrapolated (occupied ÷ daysWithData ×
    // daysInMonth); budget/actuals sources are already monthly totals.
    desc: 'Rate × occupied rooms — partial-month PMS stats are paced to the full month first',
    tag: '$ × occ rooms',
    inputHint: 'Rate applied to each forecast occupied room.',
    formula: 'Forecast = Input × Occupied Rooms',
    keywords: 'por orn per occupied room night rate',
  },
  {
    id: '$ / ARN',
    group: 'Driven by Rooms',
    label: '$PAR — $ per available room',
    desc: 'Rate × available rooms, whatever occupancy does — PMS stats are paced to the full month first',
    tag: '$ × avail rooms',
    inputHint: 'Rate applied to each available room.',
    formula: 'Forecast = Input × Available Rooms',
    keywords: 'par arn per available room night rate capacity',
  },

  // ── Driven by History ─────────────────────────────────────────────────────
  {
    id: 'LY Same Month',
    group: 'Driven by History',
    // LySameMonth.js returns lyActual flat. requiresInput() is false — there is
    // no growth %. (Growth belongs to Seasonal Budget Spread.)
    label: 'Last Year Same Month',
    desc: "Repeats last year's same-month actual, unchanged",
    tag: 'LY actual',
    inputHint: 'No input — the prior-year actual is used as-is.',
    formula: 'Forecast = Last Year Same Month Actual',
    keywords: 'ly prior year same month',
  },
  {
    id: 'Seasonal Budget Spread',
    group: 'Driven by History',
    // SeasonalBudgetSpread.js: base × (1 + adj%), base resolved per month as
    // LY actual → LY budget → this year's budget → 0. It does NOT spread an
    // annual total across months.
    label: 'Seasonal Budget Spread',
    desc: "Each month's last-year actual — or LY budget, then this year's budget — with an optional growth %",
    tag: 'LY × (1+%)',
    inputHint: 'Growth % applied to the base period (3 = +3%). Leave 0 for a flat repeat.',
    formula: 'Forecast = Base Period × (1 + Growth %)',
    keywords: 'seasonality pattern shape growth',
  },
  {
    id: '$ LM',
    group: 'Driven by History',
    // DollarLm.js: previousMonthActual is the latest CLOSED month (ContextBuilder
    // walks back), falling back to that month's forecast.
    label: '$ Last Month',
    desc: "Repeats the latest closed month's actual, walking back if last month isn't closed yet",
    tag: 'closed mo $',
    inputHint: 'No input — the latest closed month carries forward.',
    formula: 'Forecast = Latest Closed Month Actual (else its forecast)',
    keywords: 'lm prior previous month run rate',
  },
  {
    id: '$ / ORN LM',
    group: 'Driven by History',
    label: '$POR Last Month',
    desc: "Latest closed month's rate per occupied room × this month's projected rooms",
    tag: 'LM $POR',
    inputHint: 'No input — the rate is derived from last month.',
    formula: 'Forecast = (Last Month $ ÷ Last Month Occupied Rooms) × Projected Occupied Rooms',
    keywords: 'lm rate per occupied room',
  },
  {
    id: '$ / ARN LM',
    group: 'Driven by History',
    label: '$PAR Last Month',
    desc: "Latest closed month's rate per available room × this month's projected rooms",
    tag: 'LM $PAR',
    inputHint: 'No input — the rate is derived from last month.',
    formula: 'Forecast = (Last Month $ ÷ Last Month Available Rooms) × Projected Available Rooms',
    keywords: 'lm rate per available room',
  },
  {
    id: '$ / ORN LY',
    group: 'Driven by History',
    label: '$POR Last Year',
    desc: "Last year's same-month rate per occupied room × this year's projected rooms",
    tag: 'LY $POR',
    inputHint: 'No input — the rate is derived from last year.',
    formula: 'Forecast = (LY $ ÷ LY Occupied Rooms) × Projected Occupied Rooms',
    keywords: 'ly rate per occupied room',
  },
  {
    id: '$ / ARN LY',
    group: 'Driven by History',
    label: '$PAR Last Year',
    desc: "Last year's same-month rate per available room × this year's projected rooms",
    tag: 'LY $PAR',
    inputHint: 'No input — the rate is derived from last year.',
    formula: 'Forecast = (LY $ ÷ LY Available Rooms) × Projected Available Rooms',
    keywords: 'ly rate per available room',
  },

  // ── Driven by Budget ──────────────────────────────────────────────────────
  {
    id: 'Budget',
    group: 'Driven by Budget',
    label: 'Budget',
    desc: "Uses each month's budgeted amount as-is",
    tag: 'budget $',
    inputHint: 'No input — the budget is used unchanged.',
    formula: 'Forecast = Budget Amount',
    keywords: 'plan approved',
  },
  {
    id: 'Budgeted $POR',
    group: 'Driven by Budget',
    // BudgetedPor.js falls back to the raw budget when budgetOccupiedRooms or
    // projectedOccupiedRooms is 0.
    label: 'Budgeted $POR',
    desc: 'Budgeted rate per occupied room × projected rooms; falls back to the budget when budget room nights are missing',
    tag: 'Bud $POR',
    inputHint: 'No input — the rate comes from the budget.',
    formula: 'Forecast = (Budget ÷ Budget Occupied Rooms) × Projected Occupied Rooms',
    keywords: 'por budgeted per occupied room',
  },
  {
    id: 'Budgeted $PAR',
    group: 'Driven by Budget',
    label: 'Budgeted $PAR',
    desc: 'Budgeted rate per available room × projected rooms; falls back to the budget when budget room nights are missing',
    tag: 'Bud $PAR',
    inputHint: 'No input — the rate comes from the budget.',
    formula: 'Forecast = (Budget ÷ Budget Available Rooms) × Projected Available Rooms',
    keywords: 'par budgeted per available room',
  },
  {
    id: '% of Total Revenue (Budgeted)',
    group: 'Driven by Budget',
    label: '% of Total Revenue (Budgeted)',
    desc: "This account's share of total budgeted revenue, applied to total forecast revenue; falls back to the budget",
    tag: 'bud % × rev',
    inputHint: 'No input — the percentage comes from the budget.',
    formula: 'Forecast = (Budget ÷ Total Budget Rev) × Total Forecast Rev',
    keywords: 'budgeted percent revenue',
  },
  {
    id: '% of Selected Revenue (Budgeted)',
    group: 'Driven by Budget',
    // BudgetedPercentOfSelectedRevenue.js applies the SAME department selection
    // to both sides — budget revenue and forecast revenue.
    label: '% of Selected Revenue (Budgeted)',
    desc: "This account's share of the selected departments' budgeted revenue, applied to those same departments' forecast revenue",
    tag: 'bud % × sel',
    inputHint: 'No input — pick the source departments below.',
    formula: 'Forecast = (Budget ÷ Selected Budget Rev) × Selected Forecast Rev',
    keywords: 'budgeted percent selected department revenue',
  },

  // ── Driven by Revenue ─────────────────────────────────────────────────────
  {
    id: '% of Revenue',
    group: 'Driven by Revenue',
    label: '% of Total Revenue',
    desc: 'A percentage of total forecast location revenue',
    tag: '% × total rev',
    inputHint: 'Percentage of total location revenue (5 = 5%).',
    formula: 'Forecast = Total Forecast Revenue × Percentage',
    keywords: 'percent of revenue total flow through',
  },
  {
    id: '% of Selected Revenue',
    group: 'Driven by Revenue',
    // PercentOfSelectedRevenue.js: an EMPTY selection falls back to total
    // revenue — which is why it is refused on revenue accounts (circular).
    label: '% of Selected Revenue',
    desc: 'A percentage of the departments you select — selecting none uses total revenue',
    tag: '% × sel rev',
    inputHint: 'Percentage of the selected departments’ revenue (5 = 5%).',
    formula: 'Forecast = Selected Department Revenue × Percentage',
    keywords: 'percent of department revenue selected',
  },

  // ── Driven by Another Account ─────────────────────────────────────────────
  {
    id: '% of Account',
    group: 'Driven by Another Account',
    // PercentOfAccount.js sums sourceChartOfAccountIds — plural.
    label: '% of Account',
    desc: 'A percentage of the combined forecast of the accounts you pick',
    tag: '% × acct',
    inputHint: 'Percentage of the linked accounts (5 = 5%).',
    formula: 'Forecast = Sum of Selected Account Forecasts × Percentage',
    keywords: 'percent of account linked sibling gl',
  },

  // ── Composed ──────────────────────────────────────────────────────────────
  {
    id: 'Zero Base',
    group: 'Composed',
    label: 'Zero Base (Multi-Driver)',
    desc: 'Sums the layers you build — fixed $, % of revenue, % of other accounts, $POR and $PAR',
    tag: 'sum of layers',
    inputHint: 'Configured in the Zero Base builder.',
    formula: 'Forecast = Sum of all configured layers',
    keywords: 'zero based build up layers fixed variable',
  },

  // ── Labor ─────────────────────────────────────────────────────────────────
  {
    id: '$POR Projection',
    group: 'Labor',
    // DporDriver.js rate ladder: this month's labor actuals → GL actuals →
    // baseline (latest month with actuals) → prior month forecast ÷ prior Occupied Rooms.
    label: '$POR Projection',
    desc: 'Cost per occupied room × projected rooms; the rate comes from labor actuals, then GL actuals, then the last month with actuals',
    tag: '$POR × occ rooms',
    inputHint: 'No input — the rate is derived from actuals.',
    formula: 'Forecast = $POR Rate × Projected Occupied Rooms',
    keywords: 'dpor labor dollars per occupied room payroll',
  },
  {
    id: 'Labor Config',
    group: 'Labor',
    // LaborConfig.js position driverTypes: min_por, hrs_por, fte, pct_revenue.
    label: 'Labor Config (Per-Position Driver)',
    desc: 'Sums each mapped position — minutes or hours per occupied room, FTE, or % of revenue',
    tag: 'per position',
    inputHint: 'Configured per position in the Labor Config builder.',
    formula: 'Forecast = Sum of per-position labor cost',
    keywords: 'labor position payroll fte hours minutes staffing',
  },
];

for (const [id, baseId] of Object.entries(LY_BUDGET_DRIVER_BASES)) {
  const base = DRIVER_CATALOG.find(d => d.id === baseId)!;
  const basis = id.includes('$POR') ? "divided by LY published budget occupied rooms, multiplied by this budget's occupied rooms"
    : id.includes('$PAR') ? "divided by LY published budget available rooms, multiplied by this budget's available rooms"
    : id.includes('Selected') ? "divided by selected departments' LY published budget revenue, multiplied by those departments' revenue in this budget"
    : id.includes('Total') ? "divided by total LY published budget revenue, multiplied by this budget's total revenue"
    : 'for the same calendar month';
  const desc = `Uses the prior year's published budget amount ${basis}.`;
  DRIVER_CATALOG.push({ ...base, id: id as DriverType, label: id.startsWith('%') ? `LY Published Budget ${id.replace(' (LY Budget)', '')}` : id, desc,
    inputHint: `${desc} Saved rate/value overrides take precedence.`,
    formula: id.includes('$POR') ? 'Budget = (LY Published Budget ÷ LY Published Budget Occupied Rooms) × Budget Occupied Rooms'
      : id.includes('$PAR') ? 'Budget = (LY Published Budget ÷ LY Published Budget Available Rooms) × Budget Available Rooms'
      : id.includes('Selected') ? 'Budget = (LY Published Budget ÷ Selected Dept LY Published Budget Revenue) × Selected Dept Budget Revenue'
      : id.includes('Total') ? 'Budget = (LY Published Budget ÷ Total LY Published Budget Revenue) × Total Budget Revenue'
      : 'Budget = LY Published Budget',
    tag: id.includes('%') ? 'LY budget % × budget rev' : id.includes('$') ? 'LY budget rate × rooms' : 'LY published amount',
    keywords: `${base.keywords || ''} last year published budget`,
  });
}

export function isDriverVisibleForVersion(id: string, isBudget: boolean): boolean {
  if (isBudget) return !Object.values(LY_BUDGET_DRIVER_BASES).includes(id);
  return !Object.prototype.hasOwnProperty.call(LY_BUDGET_DRIVER_BASES, id);
}

const BY_ID = new Map<string, DriverCatalogEntry>(DRIVER_CATALOG.map(d => [d.id, d]));

export function getDriverEntry(id: string): DriverCatalogEntry | undefined {
  return BY_ID.get(id);
}

/**
 * Mirrors driverEligibility.js: the backend refuses ineligible assignments, so
 * an ineligible driver must never be offered.
 */
export function isDriverApplicable(driver: string, accountType: 'Revenue' | 'Expense'): boolean {
  const applicable = DRIVER_CONFIG[driver]?.applicableTo ?? 'both';
  return applicable === 'both' || applicable === accountType.toLowerCase();
}

export function driverSearchText(d: DriverCatalogEntry): string {
  return `${d.label} ${d.desc} ${d.tag} ${d.keywords || ''} ${d.id}`.toLowerCase();
}

/** Presentation only: persisted driver IDs and legacy calculations stay stable. */
export function getVersionDriverEntry(id: string, isBudget: boolean, referenceYear?: number | null): DriverCatalogEntry | undefined {
  const entry = getDriverEntry(id);
  if (!entry || !isBudget) return entry;
  if (LY_BUDGET_DRIVER_BASES[id]) return entry;
  // Existing stored Budget assignments remain readable but aren't offered for
  // new budget assignments. Their source and math are not relabeled as LY.
  if (Object.values(LY_BUDGET_DRIVER_BASES).includes(id)) return { ...entry,
    inputHint: `${entry.inputHint} This saved assignment keeps its original budget source. Choose an LY Published Budget driver to use the prior year's published budget.` };
  const live = ['OTB Forecast', 'RMS Forecast', 'REVPAR Projection', '$POR Projection', 'HPOR', 'DPOR', '$ LM', '$ / ORN LM', '$ / ARN LM'];
  const desc = live.includes(id) ? `${entry.desc} Uses live or recent data; review the source period for an annual budget.` : entry.desc;
  return { ...entry, desc, inputHint: live.includes(id) ? desc : entry.inputHint,
    formula: entry.formula.replace(/\bBudget\b/g, 'Published Budget').replace(/^Forecast\s*=/, 'Budget =').replace(/forecast revenue/g, 'budget revenue') };
}
